import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import syepMain from "../../Assets/syepMain.png";
import cf2025 from "../../Assets/syep_2025.jpeg";
import syepCareer1 from "../../Assets/syep-career-1.png";
import syepCareer2 from "../../Assets/syep-career-2.png";
import syepCareer3 from "../../Assets/syep-career-3.png";
import syepCareer4 from "../../Assets/syep-career-4.png";
import ambassadorPortrait from "../../Assets/syep14.png";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";
import SlickLazyImage from "../shared/SlickLazyImage";
import { useTranslation } from "react-i18next";

const sliderImages = [
  { src: syepMain, altKey: "programs.ambassadors.sliderAlts.0" },
  { src: cf2025, altKey: "programs.ambassadors.sliderAlts.1" },
  { src: syepCareer1, altKey: "programs.ambassadors.sliderAlts.2" },
  { src: syepCareer2, altKey: "programs.ambassadors.sliderAlts.3" },
  { src: syepCareer3, altKey: "programs.ambassadors.sliderAlts.4" },
  { src: syepCareer4, altKey: "programs.ambassadors.sliderAlts.5" },
];

function Ambassadors() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeSlide, setActiveSlide] = React.useState(0);

  const handleApply = () => {
    navigate("/registration#ambassador-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2400,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: false,
    lazyLoad: "ondemand",
    beforeChange: (_current, next) => setActiveSlide(next),
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: initiativesRef, hasEntered: initiativesSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({
    threshold: 0.18,
    once: true,
  });

  const eligibility = t("programs.ambassadors.eligibility", { returnObjects: true });
  const benefits = t("programs.ambassadors.benefits", { returnObjects: true });
  const additionalBenefits = t("programs.ambassadors.additionalBenefits", { returnObjects: true });
  const initiatives = t("programs.ambassadors.initiatives", { returnObjects: true });
  const snapshotBullets = t("programs.ambassadors.snapshot.bullets", { returnObjects: true });

  return (
    <Container fluid className="about-section ambassadors-page">
      <Particle />

      <Container className="ambassadors-container">
        <header className="ambassadors-hero">
          <h1 className="ambassadors-hero-title">
            {t("programs.ambassadors.hero.titlePrefix")} <strong className="purple">{t("programs.ambassadors.hero.titleEmphasis")}</strong>
          </h1>
          <p className="ambassadors-hero-lead">
            {t("programs.ambassadors.hero.lead")}
          </p>
          <div className="ambassadors-hero-cta">
            <Button onClick={handleApply} className="custom-yellow-btn ambassadors-apply-btn">
              {t("common.applyNow")}
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`ambassadors-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="about-top">
            <Col xs={12} md={6} className="about-top-col">
              <h2 className="about-section-heading about-section-heading--ghost" aria-hidden="true">
                {t("programs.ambassadors.snapshot.title")}
              </h2>

              <div className="policy-card about-card about-who-card ambassadors-summary-card">
                <h2 className="ambassadors-card-title">{t("programs.ambassadors.snapshot.title")}</h2>
                <p className="about-text ambassadors-card-text">
                  {t("programs.ambassadors.snapshot.text")}
                </p>
                <ul className="ambassadors-bullets">
                  {Array.isArray(snapshotBullets)
                    ? snapshotBullets.map((item) => <li key={item}>{item}</li>)
                    : null}
                </ul>

                <Button onClick={handleApply} className="custom-yellow-btn ambassadors-apply-btn ambassadors-apply-btn-secondary">
                  {t("common.applyHere")}
                </Button>
              </div>
            </Col>

            <Col xs={12} md={6} className="about-top-col">
              <h2 className="about-section-heading about-section-heading--ghost" aria-hidden="true">
                {t("programs.ambassadors.snapshot.title")}
              </h2>

              <div
                className="policy-card mission-image-card mission-slider-card mission-top-media-card about-top-media-card"
                aria-label={t("programs.ambassadors.aria.highlights")}
              >
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="mission-slide">
                      <SlickLazyImage
                        src={image.src}
                        alt={t(image.altKey)}
                        className="mission-slide-img"
                        index={index}
                        activeIndex={activeSlide}
                        total={sliderImages.length}
                        buffer={2}
                        eager
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`ambassadors-section ambassadors-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label={t("programs.ambassadors.aria.overview")}
        >
          <Row className="ambassadors-overview-row">
            <Col md={12} className="ambassadors-col">
              <div className="policy-card ambassadors-copy-card">
                <h2 className="ambassadors-section-heading">{t("programs.ambassadors.eligibilityTitle")}</h2>
                <p className="ambassadors-body">{t("programs.ambassadors.eligibilityLead")}</p>
                <ul className="ambassadors-list">
                  {Array.isArray(eligibility) ? eligibility.map((item) => <li key={item}>{item}</li>) : null}
                </ul>
              </div>
            </Col>
          </Row>

          <Row className="ambassadors-overview-row ambassadors-overview-split">
            <Col md={7} className="ambassadors-col">
              <div className="policy-card ambassadors-copy-card ambassadors-copy-card--compact">
                <h2 className="ambassadors-section-heading">{t("programs.ambassadors.benefitsTitle")}</h2>
                <p className="ambassadors-body">{t("programs.ambassadors.benefitsLead")}</p>
                <ul className="ambassadors-list">
                  {Array.isArray(benefits) ? benefits.map((item) => <li key={item}>{item}</li>) : null}
                </ul>

                <p className="ambassadors-body">
                  {t("programs.ambassadors.benefitsExtra")}
                </p>
                <ul className="ambassadors-list" aria-label={t("programs.ambassadors.aria.additionalBenefits")}>
                  {Array.isArray(additionalBenefits)
                    ? additionalBenefits.map((item) => <li key={item}>{item}</li>)
                    : null}
                </ul>
                <p className="ambassadors-body">
                  {t("programs.ambassadors.benefitsOutro")}
                </p>

                <p className="ambassadors-body ambassadors-body-muted">
                  {t("programs.ambassadors.serviceNote")}
                </p>
              </div>
            </Col>

            <Col md={5} className="ambassadors-col">
              <div className="policy-card ambassadors-image-card ambassadors-image-card--side" aria-label={t("programs.ambassadors.aria.portrait")}>
                <Tilt className="ambassadors-tilt" glareEnable={false}>
                  <img
                    src={ambassadorPortrait}
                    className="img-fluid ambassadors-image"
                    alt={t("programs.ambassadors.images.portraitAlt")}
                    loading="lazy"
                    decoding="async"
                  />
                </Tilt>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={initiativesRef}
          className={`ambassadors-section ambassadors-reveal${initiativesSeen ? " is-seen" : ""}`}
          aria-label={t("programs.ambassadors.aria.initiatives")}
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">{t("programs.ambassadors.initiativesTitle")}</h2>
          <p className="ambassadors-lead">
            {t("programs.ambassadors.initiativesLead")}
          </p>

          <div className="ambassadors-grid">
            {Array.isArray(initiatives)
              ? initiatives.map((initiative) => (
                  <div key={initiative.title} className="policy-card ambassadors-feature-card">
                    <h3 className="ambassadors-feature-title">{initiative.title}</h3>
                    <p className="ambassadors-feature-text">{initiative.text}</p>
                  </div>
                ))
              : null}
          </div>

          <p className="ambassadors-footnote">
            {t("programs.ambassadors.initiativesFootnote")}
          </p>
        </section>

        <section
          ref={videoRef}
          className={`ambassadors-section ambassadors-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label={t("programs.ambassadors.aria.video")}
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">{t("common.summerInternshipProgram")}</h2>
          <div className="policy-card ambassadors-video-card">
            <div className="ambassadors-video">
              <iframe
                title={t("programs.ambassadors.videoIframeTitle")}
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`ambassadors-testimonials-section ambassadors-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label={t("programs.ambassadors.aria.testimonials")}
        >
          <h2 className="ambassadors-section-heading ambassadors-section-heading-center">{t("common.testimonials")}</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Ambassadors;
